// ABOUTME: Main app component with routing and authentication
// ABOUTME: Defines public and protected routes

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from '@/contexts/AuthContext';
import { queryClient } from '@/lib/queryClient';
import { LoginPage } from '@/components/auth/LoginPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import OverviewPage from '@/components/overview/OverviewPage';
import { Overview } from '@/components/dashboard/Overview';
import { HypothesisPage } from '@/pages/HypothesisPage';
import { UserManagementPage } from '@/pages/UserManagementPage';
import { PersonasPage } from '@/pages/PersonasPage';
import { GlossaryPage } from '@/pages/GlossaryPage';
import { ChatPage } from '@/pages/ChatPage';
import { IngestTranscriptPage } from '@/pages/IngestTranscriptPage';
import { InterviewsPage } from '@/pages/InterviewsPage';
import { InterviewDetailPage } from '@/pages/InterviewDetailPage';
import { AnalysisPage } from '@/pages/AnalysisPage';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Nested routes inside dashboard layout */}
              <Route index element={<OverviewPage />} />
              <Route path="browse" element={<Overview />} />
              <Route path="hypothesis/:category/:id" element={<HypothesisPage />} />
              <Route path="interviews" element={<InterviewsPage />} />
              <Route path="analysis" element={<AnalysisPage />} />
              <Route path="interviews/ingest" element={<IngestTranscriptPage />} />
              <Route path="interviews/:id" element={<InterviewDetailPage />} />
              {/* Old W1 ingest route — keep bookmarks working */}
              <Route path="ingest" element={<Navigate to="/dashboard/interviews/ingest" replace />} />
              <Route path="personas" element={<PersonasPage />} />
              <Route path="glossary" element={<GlossaryPage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="chat/:threadId" element={<ChatPage />} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Redirect root to dashboard */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>

      {/* React Query DevTools (development only) */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
